import { json, postBiz } from '../core/coreapi';
import {
  normalizeSimulationManager,
  normalizeSimulationWorkbench,
} from './simulation.normalize';
import {
  IPagedResult,
  ISensitivityControl,
  ISensitivityResultRow,
  ISimulationAssumptionRow,
  ISimulationCreateScenarioRequest,
  ISimulationCreateScenarioResult,
  ISimulationDriver,
  ISimulationIssueRow,
  ISimulationManagerWorkbench,
  ISimulationWorkbench,
} from './simulation.models';

const SERVICE = 'biesimulation';

const unwrap = (response: any) => response?.sd ?? response ?? {};

const paged = <T>(response: any): IPagedResult<T> => {
  const sd = unwrap(response);
  const rows = Array.isArray(sd) ? sd : (sd.rows ?? sd.data ?? []);
  return {
    rows,
    rowCount: Number(sd.rowCount ?? sd.rowcount ?? sd.total ?? rows.length ?? 0),
    page: Number(sd.page ?? 0),
    pageSize: Number(sd.pageSize ?? sd.pagesize ?? rows.length ?? 0),
  };
};

/**
 * Planning-Cycle Simulation Manager.
 * DB: bie.spx_biesimulation_workbench(pci, user)
 */
export const fetchSimulationManager = async (
  planningcycleid: number,
): Promise<ISimulationManagerWorkbench> =>
  normalizeSimulationManager(
    await postBiz({ s: SERVICE, a: 'workbench', pci: planningcycleid }),
  );

/**
 * Explicit scenario creation.
 * DB: bie.spx_biesimulation_createscenario(...)
 */
export const createSimulationScenario = async (
  request: ISimulationCreateScenarioRequest,
): Promise<ISimulationCreateScenarioResult> => {
  const response = await postBiz({
    s: SERVICE,
    a: 'createscenario',
    pci: request.planningcycleid,
    nam: request.scenarioname,
    typ: request.scenariotypecode,
    psi: request.parentscenarioid ?? null,
    dsc: request.description ?? '',
  });
  return unwrap(response) as ISimulationCreateScenarioResult;
};

/**
 * Scenario detail workbench.
 * DB: bie.spx_biesimulation_scenarioworkbench(pci, sci, user)
 */
export const fetchSimulationScenarioWorkbench = async (
  planningcycleid: number,
  scenarioid: number,
): Promise<ISimulationWorkbench> =>
  normalizeSimulationWorkbench(
    await postBiz({
      s: SERVICE,
      a: 'scenarioworkbench',
      pci: planningcycleid,
      sci: scenarioid,
    }),
  );

// Backward-compatible alias for local callers that already have a Scenario.
export const fetchSimulationWorkbench = fetchSimulationScenarioWorkbench;

export const saveSimulationAssumptions = async (
  planningcycleid: number,
  scenarioid: number,
  records: ISimulationAssumptionRow[],
) => json(await postBiz({
  s: SERVICE,
  a: 'saveassumptions',
  pci: planningcycleid,
  sci: scenarioid,
  rcd: records.map((r) => ({
    rid: r.rid ?? null,
    rix: r.rix ?? null,
    shc: r.code,
    val: r.value ?? null,
    cnf: r.confidence ?? null,
    stp: r.stamp ?? null,
  })),
}));

export const saveSimulationDrivers = async (
  planningcycleid: number,
  scenarioid: number,
  records: ISimulationDriver[],
) => json(await postBiz({
  s: SERVICE,
  a: 'savedrivers',
  pci: planningcycleid,
  sci: scenarioid,
  rcd: records.map((r) => ({
    rid: r.driverid ?? null,
    rix: r.driverix ?? null,
    drc: r.drivercode,
    val: r.value,
    stp: r.stamp ?? null,
  })),
}));

export const recomputeSimulation = async (planningcycleid: number, scenarioid: number) =>
  json(await postBiz({ s: SERVICE, a: 'recompute', pci: planningcycleid, sci: scenarioid }));

export const runSimulationSensitivity = async (
  planningcycleid: number,
  scenarioid: number,
  records: ISensitivityControl[],
) => json(await postBiz({
  s: SERVICE,
  a: 'runsensitivity',
  pci: planningcycleid,
  sci: scenarioid,
  rcd: records.map((r) => ({ drc: r.drivercode, chp: r.changepercent })),
}));

// Retained for compatibility if paged endpoints are already wired.
export const fetchSimulationIssues = async (
  planningcycleid: number,
  scenarioid: number,
  page: number,
  pageSize: number,
  severity?: string,
): Promise<IPagedResult<ISimulationIssueRow>> =>
  paged<ISimulationIssueRow>(await postBiz({
    s: SERVICE, a: 'issues', pci: planningcycleid, sci: scenarioid,
    pgo: page * pageSize, pgl: pageSize, svr: severity || null,
  }));

export const fetchSensitivityResults = async (
  planningcycleid: number,
  scenarioid: number,
  page: number,
  pageSize: number,
): Promise<IPagedResult<ISensitivityResultRow>> =>
  paged<ISensitivityResultRow>(await postBiz({
    s: SERVICE, a: 'sensitivityresults', pci: planningcycleid,
    sci: scenarioid, pgo: page * pageSize, pgl: pageSize,
  }));

export const changeSimulationTargetSet = async (
  planningcycleid: number,
  scenarioid: number,
  targetsetid: number,
  targetsetcode?: string,
) => json(await postBiz({
  s: SERVICE, a: 'targetset', pci: planningcycleid, sci: scenarioid,
  tsi: targetsetid, tsc: targetsetcode ?? null,
}));

export const submitSimulationToExecutiveReview = async (
  planningcycleid: number,
  scenarioid: number,
  notes = '',
) => json(await postBiz({
  s: SERVICE, a: 'submitexecutivereview', pci: planningcycleid, sci: scenarioid, nts: notes,
}));

export const resubmitSimulationToExecutiveReview = async (
  planningcycleid: number,
  scenarioid: number,
  submissionid: number,
  notes = '',
) => json(await postBiz({
  s: SERVICE, a: 'resubmitexecutivereview', pci: planningcycleid, sci: scenarioid,
  sui: submissionid, nts: notes,
}));

export const acknowledgeExecutiveReturn = async (
  planningcycleid: number,
  scenarioid: number,
  submissionid: number,
) => json(await postBiz({
  s: SERVICE, a: 'acknowledgereturn', pci: planningcycleid, sci: scenarioid, sui: submissionid,
}));

/**
 * Resolve the user's current Objective Gap session for a source Scenario.
 * DB: bie.spx_biesimulation_objectivegapcurrent(scenariocode, user)
 */
export const fetchCurrentSimulationObjectiveGap = async (
  scenariocode: string,
): Promise<any> => unwrap(await postBiz({
  s: SERVICE,
  a: 'objectivegapcurrent',
  scc: scenariocode,
}));

/**
 * Start/restart Objective Gap against the current source Scenario baseline.
 * DB: bie.spx_biesimulation_objectivegapstart(scenariocode, targetvalue, mode, user)
 */
export const startSimulationObjectiveGap = async (
  scenariocode: string,
  targetvalue: number,
  computemode: 'AUTO' | 'MANUAL',
): Promise<any> => unwrap(await postBiz({
  s: SERVICE,
  a: 'objectivegapstart',
  scc: scenariocode,
  tgv: targetvalue,
  cmd: computemode,
}));

/**
 * Objective Gap workbench.
 * DB: bie.spx_biesimulation_objectivegapworkbench(sessioncode, user)
 */
export const fetchSimulationObjectiveGapWorkbench = async (
  sessioncode: string,
): Promise<any> => unwrap(await postBiz({
  s: SERVICE,
  a: 'objectivegapworkbench',
  ogc: sessioncode,
}));

/**
 * Change Objective Gap evaluation mode without resetting the ASI workspace.
 * DB: bie.spx_biesimulation_objectivegapsetmode(sessioncode, mode, user)
 */
export const setSimulationObjectiveGapMode = async (
  sessioncode: string,
  computemode: 'AUTO' | 'MANUAL',
): Promise<any> => unwrap(await postBiz({
  s: SERVICE,
  a: 'objectivegapsetmode',
  ogc: sessioncode,
  cmd: computemode,
}));

/**
 * Evaluate one governed Objective Gap management lever.
 * DB: bie.spx_biesimulation_objectivegapevaluate(sessioncode, drivercode, value, user)
 */
export const evaluateSimulationObjectiveGap = async (
  sessioncode: string,
  drivercode: string,
  value: number,
): Promise<any> => unwrap(await postBiz({
  s: SERVICE,
  a: 'objectivegapevaluate',
  ogc: sessioncode,
  drc: drivercode,
  val: value,
}));
